/**
 * Branded loader — visual twin of the static boot fallback in each SPA's
 * `index.html`. Fullscreen mode uses `position:fixed` so the logo stays
 * pinned to the viewport center across every boot phase (static HTML →
 * React auth gate → context hydrate) without the "shifts up" jump caused
 * by `100vh` vs `100dvh`, body safe-area padding, or shell layout mount.
 */

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from './cx';

const BOOT_STYLE_ID = 'pc-boot-loader-keyframes';
const LOGO_SRC = '/brand/boot-logo.png';
/** Matches `theme('colors.paper.DEFAULT')` / Tailwind `bg-paper`. */
const PAPER = '#F2EAD9';
const MUTED = '#6E6051';
const INK = '#1B1714';

const breatheStyle: CSSProperties = {
  animation: 'pc-boot-breathe 1.6s ease-in-out infinite alternate',
};

const sweepStyle: CSSProperties = {
  animation: 'pc-boot-sweep 1.2s cubic-bezier(.45, 0, .55, 1) infinite',
};

const labelStyle: CSSProperties = {
  margin: 0,
  fontSize: 12,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: MUTED,
  fontFamily: 'Inter, -apple-system, system-ui, sans-serif',
};

function ensureBootLoaderStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(BOOT_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = BOOT_STYLE_ID;
  el.textContent = `
    @keyframes pc-boot-breathe { from { transform: scale(1); opacity: .82; } to { transform: scale(1.03); opacity: 1; } }
    @keyframes pc-boot-sweep { from { transform: translateX(-105%); } to { transform: translateX(285%); } }
    @keyframes pc-boot-reveal { to { opacity: 1; } }
    @media (prefers-reduced-motion: reduce) {
      .pc-boot-anim { animation-iteration-count: 1 !important; }
    }
  `;
  document.head.appendChild(el);
}

// Inject keyframes synchronously on first import — not in useEffect — so
// the first React paint already matches the static HTML boot animation.
ensureBootLoaderStyles();

export interface BootLoaderProps {
  label?: string;
  className?: string;
  /**
   * Full-viewport fixed overlay (boot / auth / context gates).
   * When false, centers inside the parent — use for in-shell Suspense.
   */
  fullscreen?: boolean;
  showDelayedHint?: boolean;
}

export function BootLoader({
  label,
  className,
  fullscreen = true,
  showDelayedHint,
}: BootLoaderProps) {
  // Default-value form so the loader still renders English if it mounts
  // before the i18n catalogs finish initializing (it IS the boot screen).
  const { t } = useTranslation();
  const hint = showDelayedHint ?? fullscreen;
  const labelText = label ?? t('ui.bootLoader.loading', 'Loading');

  return (
    <div
      className={cx(
        fullscreen
          ? 'fixed inset-0 z-[9999] grid place-items-center'
          : 'grid min-h-[40vh] w-full place-items-center py-16',
        className,
      )}
      style={{ backgroundColor: PAPER, color: INK }}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="px-6 py-6 text-center">
        <img
          className="pc-boot-anim mx-auto block h-10 w-auto"
          src={LOGO_SRC}
          alt="PeptideClients"
          width={160}
          height={40}
          style={{ ...breatheStyle, margin: '0 auto 18px' }}
        />
        <div
          aria-hidden
          className="mx-auto overflow-hidden rounded-full"
          style={{
            marginBottom: 14,
            height: 2,
            width: 72,
            backgroundColor: 'rgba(27, 23, 20, 0.15)',
          }}
        >
          <div
            className="pc-boot-anim h-full rounded-full"
            style={{
              ...sweepStyle,
              width: '36%',
              backgroundColor: INK,
            }}
          />
        </div>
        <p style={labelStyle}>{labelText}</p>
        {hint ? (
          <p
            className="pc-boot-anim"
            style={{
              margin: '12px 0 0',
              minHeight: '2.5rem',
              fontSize: 13,
              color: MUTED,
              opacity: 0,
              animation: 'pc-boot-reveal 0.3s ease 6s forwards',
            }}
          >
            {t('ui.bootLoader.delayedHint', 'Taking longer than expected?')}{' '}
            <a href="" style={{ color: INK, textDecoration: 'underline' }}>
              {t('ui.bootLoader.reloadLink', 'Reload the page')}
            </a>
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** In-shell route chunk loader — does not cover the sidebar chrome. */
export function RouteContentLoader(props: Omit<BootLoaderProps, 'fullscreen' | 'showDelayedHint'>) {
  return <BootLoader fullscreen={false} showDelayedHint={false} {...props} />;
}

export interface AppBootOverlayProps {
  /** While true, the fixed boot loader stays on top of `children`. */
  active: boolean;
  children: ReactNode;
}

/**
 * Keeps one fixed boot loader visible while auth/context hydrate, then
 * cross-fades it out after the shell has painted underneath — avoids
 * the jarring jump from centered loader to sidebar layout.
 */
export function AppBootOverlay({ active, children }: AppBootOverlayProps) {
  const [visible, setVisible] = useState(true);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (active) {
      setVisible(true);
      setFading(false);
      return;
    }
    let timeoutId = 0;
    let innerRaf = 0;
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(() => {
        setFading(true);
        timeoutId = window.setTimeout(() => setVisible(false), 220);
      });
    });
    return () => {
      cancelAnimationFrame(outerRaf);
      if (innerRaf) cancelAnimationFrame(innerRaf);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [active]);

  return (
    <>
      {children}
      {visible ? (
        <BootLoader
          className={
            fading
              ? 'pointer-events-none opacity-0 transition-opacity duration-200 ease-out'
              : undefined
          }
        />
      ) : null}
    </>
  );
}
